import React, { useState, useMemo } from 'react';
import Icon from 'components/AppIcon';
import Input from 'components/ui/Input';
import Button from 'components/ui/Button';

export default function ROICalculator() {
  const [buyPrice, setBuyPrice] = useState('');
  const [currentPrice, setCurrentPrice] = useState('');
  const [shares, setShares] = useState('');
  const [calculated, setCalculated] = useState(null);

  const calculate = () => {
    const bp = parseFloat(buyPrice);
    const cp = parseFloat(currentPrice);
    const sh = parseFloat(shares);
    if (!bp || !cp || !sh || bp <= 0 || cp <= 0 || sh <= 0) return;
    const invested = bp * sh;
    const currentVal = cp * sh;
    const gainLoss = currentVal - invested;
    const roiPercent = ((gainLoss) / invested) * 100;
    setCalculated({ invested, currentVal, gainLoss, roiPercent });
  };

  const reset = () => {
    setBuyPrice(''); setCurrentPrice(''); setShares(''); setCalculated(null);
  };

  const isPositive = calculated && calculated?.gainLoss >= 0;

  return (
    <div className="rounded-xl border p-4 md:p-6" style={{ borderColor: 'var(--color-border)', background: 'var(--color-card)' }}>
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'rgba(133,113,77,0.1)' }}>
          <Icon name="Calculator" size={16} color="var(--color-accent)" />
        </div>
        <h2 className="font-heading font-semibold text-lg" style={{ color: 'var(--color-foreground)' }}>ROI Calculator</h2>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
        <Input label="Buy Price ($)" type="number" placeholder="0.00" value={buyPrice}
          onChange={e => setBuyPrice(e?.target?.value)} />
        <Input label="Current Price ($)" type="number" placeholder="0.00" value={currentPrice}
          onChange={e => setCurrentPrice(e?.target?.value)} />
        <Input label="Number of Shares" type="number" placeholder="0" value={shares}
          onChange={e => setShares(e?.target?.value)} />
      </div>
      <div className="flex gap-2 mb-4">
        <Button variant="default" onClick={calculate} iconName="Calculator" iconPosition="left">Calculate</Button>
        <Button variant="outline" onClick={reset}>Reset</Button>
      </div>
      {calculated && (
        <div className={`rounded-xl p-4 border-2 transition-all`}
          style={{ borderColor: isPositive ? '#059669' : '#DC2626', background: isPositive ? 'rgba(5,150,105,0.05)' : 'rgba(220,38,38,0.05)' }}>
          <div className="flex items-center gap-2 mb-3">
            <Icon name={isPositive ? 'TrendingUp' : 'TrendingDown'} size={18} color={isPositive ? '#059669' : '#DC2626'} />
            <span className="font-caption font-semibold text-sm" style={{ color: isPositive ? '#059669' : '#DC2626' }}>
              {isPositive ? 'Profitable Investment' : 'Loss Position'}
            </span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: 'Amount Invested', value: `$${calculated?.invested?.toLocaleString('en-US', { minimumFractionDigits: 2 })}` },
              { label: 'Current Value', value: `$${calculated?.currentVal?.toLocaleString('en-US', { minimumFractionDigits: 2 })}` },
              { label: 'Gain / Loss', value: `${isPositive ? '+' : ''}$${calculated?.gainLoss?.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, highlight: true },
              { label: 'ROI %', value: `${isPositive ? '+' : ''}${calculated?.roiPercent?.toFixed(2)}%`, highlight: true },
            ]?.map(item => (
              <div key={item?.label}>
                <p className="font-caption text-xs mb-1" style={{ color: 'var(--color-muted-foreground)' }}>{item?.label}</p>
                <p className={`font-data font-bold text-sm md:text-base ${item?.highlight ? (isPositive ? 'text-emerald-600' : 'text-red-600') : ''}`}
                  style={!item?.highlight ? { color: 'var(--color-foreground)' } : {}}>
                  {item?.value}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}